import { useEffect, useState } from "react";
import { useParams } from "react-router";
import type { Profile as ProfileT } from "../../shared/types.ts";
import { EmptyState } from "../components/EmptyState.tsx";
import { ApiError, api } from "../lib/api.ts";
import { useI18n } from "../lib/i18n.tsx";

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return (
    parts.length >= 2
      ? `${parts[0]![0]}${parts[parts.length - 1]![0]}`
      : (parts[0] ?? "?").slice(0, 2)
  ).toUpperCase();
}

export function Profile() {
  const { handle = "" } = useParams();
  const { t, locale } = useI18n();
  const [state, setState] = useState<
    { status: "loading" } | { status: "missing" } | { status: "ok"; p: ProfileT }
  >({ status: "loading" });

  useEffect(() => {
    let live = true;
    api<ProfileT>(`/api/u/${encodeURIComponent(handle)}`)
      .then((p) => live && setState({ status: "ok", p }))
      .catch(
        (e: unknown) =>
          live &&
          setState({ status: e instanceof ApiError && e.status === 404 ? "missing" : "missing" }),
      );
    return () => {
      live = false;
    };
  }, [handle]);

  if (state.status === "loading") return <div className="page muted">{t("common.loading")}</div>;
  if (state.status === "missing")
    return (
      <div className="page">
        <EmptyState icon="?" text={t("error.not_found")} cta={{ label: t("nav.home"), to: "/" }} />
      </div>
    );
  const p = state.p;
  if (p.deleted) {
    return (
      <div className="page page--narrow">
        <h1 className="h1 muted" data-testid="profile-deleted">
          {t("user.deleted")}
        </h1>
      </div>
    );
  }
  const year = new Date(p.member_since * 1000).getFullYear();
  const cityName = p.city ? (locale === "vi" ? p.city.name_vi : p.city.name_en) : null;
  const hours = (s: number) => Math.max(1, Math.round(s / 3600));
  return (
    <div className="page page--narrow">
      <header className="profile__head">
        <div className="avatar" aria-hidden="true">
          {initials(p.display_name || p.handle)}
        </div>
        <div>
          <h1 className="h1" data-testid="profile-name">
            {p.display_name || p.handle}
          </h1>
          <p className="muted">
            @{p.handle}
            {cityName ? ` · ${cityName}` : ""} · {t("common.since", { year })}
          </p>
        </div>
      </header>

      {p.seller ? (
        <section className="section" data-testid="seller-block">
          <h2 className="h2">{t("profile.as_seller")}</h2>
          <p>
            {t("profile.sold", { n: p.seller.completed_sales })}
            {p.seller.sell_through_pct !== null
              ? ` · ${t("profile.sell_through", { pct: `${p.seller.sell_through_pct}%` })}`
              : ""}
            {p.seller.median_share_s !== null
              ? ` · ${t("profile.share_time", { hours: hours(p.seller.median_share_s) })}`
              : ""}
          </p>
          {p.seller.completed_sales === 0 ? <p className="muted small">{t("seller.new")}</p> : null}
          {p.seller.cancels_90d > 0 ? (
            <p className="muted small">
              {t("profile.seller_cancels", { n: p.seller.cancels_90d })}
            </p>
          ) : null}
          <p className="muted small">
            {t("profile.active_listings")}: {p.seller.active_listings}
          </p>
        </section>
      ) : null}

      <section className="section" data-testid="buyer-block">
        <h2 className="h2">{t("profile.as_buyer")}</h2>
        <p>
          {t("profile.bought", { n: p.buyer.completed_purchases })} ·{" "}
          {p.buyer.line === "reliable" ? (
            <span className="tag tag--ok">{t("buyer.reliable")}</span>
          ) : p.buyer.line === "noshows" ? (
            <span className="error">{t("buyer.noshows", { n: p.buyer.no_shows_90d })}</span>
          ) : (
            t("buyer.new")
          )}
        </p>
      </section>

      {p.own ? (
        <section className="section" data-testid="own-strikes">
          <h2 className="h2">{t("profile.strikes")}</h2>
          {p.own.strikes.length === 0 ? (
            <p className="muted small">0</p>
          ) : (
            <ul className="list">
              {p.own.strikes.map((s) => (
                <li key={`${s.reason}-${s.created_at}`} className="list__row">
                  <span>
                    {t(`problem.reason.${s.reason === "problem_upheld" ? "other" : s.reason}`)}
                  </span>
                  <span className="hint">
                    {t("profile.strike.expires", {
                      date: new Date(s.expires_at * 1000).toLocaleDateString("vi-VN"),
                    })}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}
    </div>
  );
}
